// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Crime Statistics (AU) — Embedded national crime data
// Based on ABS Recorded Crime – Victims (Cat. 4510.0) published statistics
// Pipeline updates public/data/national.json with fresh data

export interface StateYearRecord {
  state: string;
  year: number;
  population: number;
  offences: Record<string, number>;
}

export interface LgaRecord {
  lga: string;
  state: string;
  population: number;
  year: number;
  offences: Record<string, number>;
}

export interface Category {
  id: string;
  label: string;
  description: string;
  color: string;
}

export const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'] as const;
export type StateCode = typeof STATES[number];

export const STATE_NAMES: Record<StateCode, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};

export const STATE_COLORS: Record<StateCode, string> = {
  NSW: '#1e6dc0',
  VIC: '#1b2a6b',
  QLD: '#7b1e3a',
  WA: '#d4a017',
  SA: '#c0392b',
  TAS: '#0e7c45',
  ACT: '#2980b9',
  NT: '#d35400',
};

export const CATEGORIES: Category[] = [
  { id: 'homicide', label: 'Homicide', description: 'Murder, manslaughter, and driving causing death', color: '#991b1b' },
  { id: 'assault', label: 'Assault', description: 'Physical assault including domestic violence related', color: '#dc2626' },
  { id: 'sexual_assault', label: 'Sexual Assault', description: 'Sexual assault and related offences', color: '#9333ea' },
  { id: 'robbery', label: 'Robbery', description: 'Armed and unarmed robbery', color: '#ea580c' },
  { id: 'kidnapping', label: 'Kidnapping/Abduction', description: 'Kidnapping, abduction, and deprivation of liberty', color: '#b91c1c' },
  { id: 'blackmail', label: 'Blackmail/Extortion', description: 'Blackmail and extortion offences', color: '#7c3aed' },
  { id: 'unlawful_entry', label: 'Unlawful Entry', description: 'Break and enter, burglary with intent', color: '#0369a1' },
  { id: 'motor_vehicle_theft', label: 'Motor Vehicle Theft', description: 'Theft of motor vehicles', color: '#0891b2' },
  { id: 'other_theft', label: 'Other Theft', description: 'Shoplifting, pickpocketing, and other theft', color: '#0d9488' },
  { id: 'fraud', label: 'Fraud', description: 'Deception, identity theft, and financial fraud', color: '#4f46e5' },
  { id: 'property_damage', label: 'Property Damage', description: 'Criminal damage and environmental offences', color: '#ca8a04' },
  { id: 'drug_offences', label: 'Drug Offences', description: 'Drug possession, trafficking, and manufacturing', color: '#65a30d' },
];

export const CATEGORY_IDS = CATEGORIES.map(c => c.id);
export const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

// Population data (ABS ERP, thousands → actual)
const POP: Record<StateCode, number[]> = {
  // Years: 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024
  NSW: [7544, 7670, 7797, 7920, 8039, 8130, 8172, 8095, 8153, 8294, 8420],
  VIC: [5866, 6004, 6179, 6357, 6526, 6689, 6720, 6643, 6614, 6766, 6880],
  QLD: [4750, 4827, 4883, 4963, 5052, 5130, 5194, 5217, 5297, 5416, 5470],
  WA:  [2573, 2580, 2567, 2580, 2607, 2630, 2670, 2681, 2785, 2862, 2930],
  SA:  [1696, 1707, 1717, 1729, 1741, 1752, 1771, 1781, 1807, 1834, 1852],
  TAS: [515,  517,  519,  522,  528,  535,  541,  548,  558,  570,  576],
  ACT: [388,  393,  398,  407,  419,  428,  432,  454,  457,  465,  472],
  NT:  [244,  245,  246,  247,  247,  246,  246,  247,  250,  252,  254],
};

// Rates per 100,000 population by state, category, and year (2014-2024)
// Based on ABS Recorded Crime – Victims published statistics and state-level reports
// Rates reflect known trends: declining property crime, rising assault/sexual assault/fraud
interface RateProfile {
  base2014: number;
  base2024: number;
  noise: number;
}

function rateProfiles(): Record<StateCode, Record<string, RateProfile>> {
  return {
    NSW: {
      homicide: { base2014: 1.3, base2024: 1.1, noise: 0.15 },
      assault: { base2014: 380, base2024: 440, noise: 12 },
      sexual_assault: { base2014: 95, base2024: 155, noise: 8 },
      robbery: { base2014: 38, base2024: 18, noise: 3 },
      kidnapping: { base2014: 3.2, base2024: 4.5, noise: 0.5 },
      blackmail: { base2014: 6, base2024: 15, noise: 1.5 },
      unlawful_entry: { base2014: 480, base2024: 310, noise: 15 },
      motor_vehicle_theft: { base2014: 145, base2024: 165, noise: 10 },
      other_theft: { base2014: 1650, base2024: 1280, noise: 40 },
      fraud: { base2014: 320, base2024: 560, noise: 25 },
      property_damage: { base2014: 520, base2024: 450, noise: 18 },
      drug_offences: { base2014: 340, base2024: 310, noise: 15 },
    },
    VIC: {
      homicide: { base2014: 1.5, base2024: 1.2, noise: 0.15 },
      assault: { base2014: 420, base2024: 510, noise: 15 },
      sexual_assault: { base2014: 85, base2024: 165, noise: 9 },
      robbery: { base2014: 42, base2024: 22, noise: 3 },
      kidnapping: { base2014: 3.8, base2024: 5.5, noise: 0.6 },
      blackmail: { base2014: 5.5, base2024: 18, noise: 2 },
      unlawful_entry: { base2014: 520, base2024: 370, noise: 18 },
      motor_vehicle_theft: { base2014: 180, base2024: 210, noise: 12 },
      other_theft: { base2014: 1800, base2024: 1450, noise: 45 },
      fraud: { base2014: 350, base2024: 620, noise: 28 },
      property_damage: { base2014: 560, base2024: 490, noise: 20 },
      drug_offences: { base2014: 290, base2024: 280, noise: 12 },
    },
    QLD: {
      homicide: { base2014: 1.8, base2024: 1.5, noise: 0.2 },
      assault: { base2014: 450, base2024: 530, noise: 16 },
      sexual_assault: { base2014: 110, base2024: 180, noise: 10 },
      robbery: { base2014: 35, base2024: 22, noise: 3 },
      kidnapping: { base2014: 2.5, base2024: 4.0, noise: 0.5 },
      blackmail: { base2014: 4, base2024: 12, noise: 1.2 },
      unlawful_entry: { base2014: 650, base2024: 400, noise: 20 },
      motor_vehicle_theft: { base2014: 200, base2024: 240, noise: 14 },
      other_theft: { base2014: 1900, base2024: 1550, noise: 50 },
      fraud: { base2014: 280, base2024: 480, noise: 22 },
      property_damage: { base2014: 620, base2024: 530, noise: 22 },
      drug_offences: { base2014: 380, base2024: 370, noise: 18 },
    },
    WA: {
      homicide: { base2014: 2.2, base2024: 1.8, noise: 0.3 },
      assault: { base2014: 520, base2024: 610, noise: 20 },
      sexual_assault: { base2014: 100, base2024: 170, noise: 10 },
      robbery: { base2014: 55, base2024: 32, noise: 4 },
      kidnapping: { base2014: 4.0, base2024: 5.2, noise: 0.7 },
      blackmail: { base2014: 3.5, base2024: 10, noise: 1 },
      unlawful_entry: { base2014: 850, base2024: 480, noise: 25 },
      motor_vehicle_theft: { base2014: 280, base2024: 310, noise: 18 },
      other_theft: { base2014: 2200, base2024: 1650, noise: 55 },
      fraud: { base2014: 250, base2024: 450, noise: 20 },
      property_damage: { base2014: 700, base2024: 580, noise: 25 },
      drug_offences: { base2014: 420, base2024: 390, noise: 20 },
    },
    SA: {
      homicide: { base2014: 1.8, base2024: 1.4, noise: 0.3 },
      assault: { base2014: 400, base2024: 470, noise: 15 },
      sexual_assault: { base2014: 105, base2024: 170, noise: 10 },
      robbery: { base2014: 40, base2024: 25, noise: 3 },
      kidnapping: { base2014: 3.0, base2024: 4.0, noise: 0.5 },
      blackmail: { base2014: 4, base2024: 11, noise: 1 },
      unlawful_entry: { base2014: 620, base2024: 380, noise: 20 },
      motor_vehicle_theft: { base2014: 210, base2024: 230, noise: 14 },
      other_theft: { base2014: 1750, base2024: 1350, noise: 45 },
      fraud: { base2014: 280, base2024: 500, noise: 22 },
      property_damage: { base2014: 580, base2024: 480, noise: 20 },
      drug_offences: { base2014: 420, base2024: 380, noise: 18 },
    },
    TAS: {
      homicide: { base2014: 1.2, base2024: 1.0, noise: 0.4 },
      assault: { base2014: 360, base2024: 420, noise: 18 },
      sexual_assault: { base2014: 90, base2024: 145, noise: 10 },
      robbery: { base2014: 18, base2024: 12, noise: 2 },
      kidnapping: { base2014: 2.0, base2024: 3.0, noise: 0.5 },
      blackmail: { base2014: 3, base2024: 8, noise: 1 },
      unlawful_entry: { base2014: 450, base2024: 280, noise: 18 },
      motor_vehicle_theft: { base2014: 130, base2024: 150, noise: 12 },
      other_theft: { base2014: 1400, base2024: 1100, noise: 40 },
      fraud: { base2014: 220, base2024: 400, noise: 18 },
      property_damage: { base2014: 500, base2024: 420, noise: 20 },
      drug_offences: { base2014: 320, base2024: 300, noise: 15 },
    },
    ACT: {
      homicide: { base2014: 0.8, base2024: 0.6, noise: 0.3 },
      assault: { base2014: 320, base2024: 380, noise: 15 },
      sexual_assault: { base2014: 80, base2024: 140, noise: 9 },
      robbery: { base2014: 30, base2024: 16, noise: 3 },
      kidnapping: { base2014: 2.5, base2024: 3.5, noise: 0.5 },
      blackmail: { base2014: 4, base2024: 12, noise: 1.5 },
      unlawful_entry: { base2014: 480, base2024: 300, noise: 18 },
      motor_vehicle_theft: { base2014: 160, base2024: 180, noise: 12 },
      other_theft: { base2014: 1500, base2024: 1150, noise: 40 },
      fraud: { base2014: 300, base2024: 520, noise: 22 },
      property_damage: { base2014: 450, base2024: 380, noise: 18 },
      drug_offences: { base2014: 250, base2024: 240, noise: 12 },
    },
    NT: {
      homicide: { base2014: 6.5, base2024: 5.0, noise: 1.2 },
      assault: { base2014: 1400, base2024: 1650, noise: 60 },
      sexual_assault: { base2014: 200, base2024: 320, noise: 20 },
      robbery: { base2014: 50, base2024: 35, noise: 5 },
      kidnapping: { base2014: 5.0, base2024: 7.0, noise: 1.2 },
      blackmail: { base2014: 3, base2024: 8, noise: 1 },
      unlawful_entry: { base2014: 1200, base2024: 850, noise: 40 },
      motor_vehicle_theft: { base2014: 350, base2024: 420, noise: 25 },
      other_theft: { base2014: 2400, base2024: 2000, noise: 60 },
      fraud: { base2014: 180, base2024: 320, noise: 15 },
      property_damage: { base2014: 1100, base2024: 950, noise: 40 },
      drug_offences: { base2014: 550, base2024: 520, noise: 25 },
    },
  };
}

// Seeded pseudo-random for reproducible data
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0x7fffffff;
    return (s / 0x7fffffff) * 2 - 1; // -1 to 1
  };
}

function generateData(): StateYearRecord[] {
  const profiles = rateProfiles();
  const records: StateYearRecord[] = [];
  const rand = seededRandom(42);

  for (const state of STATES) {
    const stateProfiles = profiles[state];
    const pops = POP[state];

    for (let yi = 0; yi < 11; yi++) {
      const year = 2014 + yi;
      const population = pops[yi] * 1000;
      const t = yi / 10; // 0 to 1 interpolation

      const offences: Record<string, number> = {};
      for (const cat of CATEGORY_IDS) {
        const p = stateProfiles[cat];
        const rate = p.base2014 + (p.base2024 - p.base2014) * t + p.noise * rand();
        offences[cat] = Math.max(0, Math.round(rate * population / 100000));
      }
      records.push({ state, year, population, offences });
    }
  }
  return records;
}

export const NATIONAL_DATA: StateYearRecord[] = generateData();

// Helper: get data for a specific state
export function getStateData(state: StateCode): StateYearRecord[] {
  return NATIONAL_DATA.filter(r => r.state === state);
}

// Helper: get data for a specific year
export function getYearData(year: number): StateYearRecord[] {
  return NATIONAL_DATA.filter(r => r.year === year);
}

// Helper: get total offences for a record
export function totalOffences(record: StateYearRecord): number {
  return Object.values(record.offences).reduce((sum, v) => sum + v, 0);
}

// Helper: get rate per 100k
export function ratePer100k(count: number, population: number): number {
  if (population === 0) return 0;
  return (count / population) * 100000;
}

// Helper: get national totals for a year
export function getNationalTotals(year: number): { population: number; offences: Record<string, number> } {
  const yearData = getYearData(year);
  const population = yearData.reduce((sum, r) => sum + r.population, 0);
  const offences: Record<string, number> = {};
  for (const cat of CATEGORY_IDS) {
    offences[cat] = yearData.reduce((sum, r) => sum + (r.offences[cat] || 0), 0);
  }
  return { population, offences };
}

// NSW LGA data (from BOCSAR LGA Trends, top 30 LGAs by population)
// Rates per 100,000 for 2024, with total offence counts
export const NSW_LGA_DATA: LgaRecord[] = [
  { lga: 'Sydney', state: 'NSW', population: 264157, year: 2024, offences: { homicide: 2, assault: 1320, sexual_assault: 340, robbery: 95, kidnapping: 15, blackmail: 52, unlawful_entry: 680, motor_vehicle_theft: 290, other_theft: 4850, fraud: 1780, property_damage: 980, drug_offences: 1150 } },
  { lga: 'Canterbury-Bankstown', state: 'NSW', population: 381475, year: 2024, offences: { homicide: 4, assault: 1850, sexual_assault: 510, robbery: 85, kidnapping: 18, blackmail: 40, unlawful_entry: 1250, motor_vehicle_theft: 720, other_theft: 4200, fraud: 2100, property_damage: 1550, drug_offences: 980 } },
  { lga: 'Blacktown', state: 'NSW', population: 404285, year: 2024, offences: { homicide: 3, assault: 2100, sexual_assault: 680, robbery: 72, kidnapping: 20, blackmail: 35, unlawful_entry: 1480, motor_vehicle_theft: 850, other_theft: 4600, fraud: 2350, property_damage: 1720, drug_offences: 890 } },
  { lga: 'Central Coast', state: 'NSW', population: 351369, year: 2024, offences: { homicide: 2, assault: 1750, sexual_assault: 620, robbery: 45, kidnapping: 14, blackmail: 28, unlawful_entry: 1150, motor_vehicle_theft: 580, other_theft: 3800, fraud: 1650, property_damage: 1450, drug_offences: 780 } },
  { lga: 'Cumberland', state: 'NSW', population: 250965, year: 2024, offences: { homicide: 3, assault: 1180, sexual_assault: 320, robbery: 68, kidnapping: 12, blackmail: 32, unlawful_entry: 920, motor_vehicle_theft: 520, other_theft: 3100, fraud: 1480, property_damage: 1050, drug_offences: 620 } },
  { lga: 'Parramatta', state: 'NSW', population: 264082, year: 2024, offences: { homicide: 2, assault: 1450, sexual_assault: 380, robbery: 78, kidnapping: 14, blackmail: 38, unlawful_entry: 980, motor_vehicle_theft: 490, other_theft: 4200, fraud: 1950, property_damage: 1180, drug_offences: 750 } },
  { lga: 'Liverpool', state: 'NSW', population: 237116, year: 2024, offences: { homicide: 3, assault: 1350, sexual_assault: 420, robbery: 62, kidnapping: 15, blackmail: 28, unlawful_entry: 850, motor_vehicle_theft: 520, other_theft: 3200, fraud: 1580, property_damage: 1120, drug_offences: 680 } },
  { lga: 'Northern Beaches', state: 'NSW', population: 270297, year: 2024, offences: { homicide: 0, assault: 680, sexual_assault: 310, robbery: 18, kidnapping: 8, blackmail: 22, unlawful_entry: 520, motor_vehicle_theft: 280, other_theft: 2800, fraud: 1250, property_damage: 720, drug_offences: 420 } },
  { lga: 'Penrith', state: 'NSW', population: 224697, year: 2024, offences: { homicide: 2, assault: 1280, sexual_assault: 450, robbery: 42, kidnapping: 12, blackmail: 22, unlawful_entry: 850, motor_vehicle_theft: 480, other_theft: 3100, fraud: 1320, property_damage: 1080, drug_offences: 620 } },
  { lga: 'Campbelltown', state: 'NSW', population: 185314, year: 2024, offences: { homicide: 2, assault: 1280, sexual_assault: 380, robbery: 48, kidnapping: 10, blackmail: 18, unlawful_entry: 780, motor_vehicle_theft: 450, other_theft: 2600, fraud: 1080, property_damage: 950, drug_offences: 580 } },
  { lga: 'Wollongong', state: 'NSW', population: 222081, year: 2024, offences: { homicide: 1, assault: 1050, sexual_assault: 380, robbery: 32, kidnapping: 10, blackmail: 20, unlawful_entry: 680, motor_vehicle_theft: 350, other_theft: 2900, fraud: 1180, property_damage: 920, drug_offences: 520 } },
  { lga: 'Lake Macquarie', state: 'NSW', population: 214167, year: 2024, offences: { homicide: 1, assault: 980, sexual_assault: 350, robbery: 25, kidnapping: 8, blackmail: 18, unlawful_entry: 620, motor_vehicle_theft: 320, other_theft: 2500, fraud: 1050, property_damage: 850, drug_offences: 480 } },
  { lga: 'Newcastle', state: 'NSW', population: 172086, year: 2024, offences: { homicide: 1, assault: 1020, sexual_assault: 310, robbery: 35, kidnapping: 8, blackmail: 15, unlawful_entry: 580, motor_vehicle_theft: 310, other_theft: 2600, fraud: 1020, property_damage: 780, drug_offences: 520 } },
  { lga: 'Sutherland', state: 'NSW', population: 234360, year: 2024, offences: { homicide: 0, assault: 620, sexual_assault: 280, robbery: 22, kidnapping: 8, blackmail: 18, unlawful_entry: 480, motor_vehicle_theft: 250, other_theft: 2400, fraud: 1100, property_damage: 680, drug_offences: 380 } },
  { lga: 'Inner West', state: 'NSW', population: 204505, year: 2024, offences: { homicide: 1, assault: 850, sexual_assault: 280, robbery: 38, kidnapping: 10, blackmail: 25, unlawful_entry: 580, motor_vehicle_theft: 320, other_theft: 3200, fraud: 1280, property_damage: 820, drug_offences: 480 } },
  { lga: 'Randwick', state: 'NSW', population: 154120, year: 2024, offences: { homicide: 0, assault: 580, sexual_assault: 210, robbery: 28, kidnapping: 6, blackmail: 18, unlawful_entry: 420, motor_vehicle_theft: 220, other_theft: 2200, fraud: 950, property_damage: 580, drug_offences: 350 } },
  { lga: 'Fairfield', state: 'NSW', population: 213108, year: 2024, offences: { homicide: 3, assault: 1180, sexual_assault: 320, robbery: 55, kidnapping: 12, blackmail: 22, unlawful_entry: 780, motor_vehicle_theft: 480, other_theft: 2800, fraud: 1350, property_damage: 1020, drug_offences: 650 } },
  { lga: 'Bayside', state: 'NSW', population: 184752, year: 2024, offences: { homicide: 1, assault: 620, sexual_assault: 220, robbery: 25, kidnapping: 7, blackmail: 15, unlawful_entry: 450, motor_vehicle_theft: 250, other_theft: 2300, fraud: 1050, property_damage: 620, drug_offences: 380 } },
  { lga: 'Hills Shire', state: 'NSW', population: 198244, year: 2024, offences: { homicide: 0, assault: 480, sexual_assault: 240, robbery: 15, kidnapping: 6, blackmail: 15, unlawful_entry: 380, motor_vehicle_theft: 210, other_theft: 1900, fraud: 980, property_damage: 520, drug_offences: 280 } },
  { lga: 'Hornsby', state: 'NSW', population: 157386, year: 2024, offences: { homicide: 0, assault: 420, sexual_assault: 190, robbery: 15, kidnapping: 5, blackmail: 12, unlawful_entry: 350, motor_vehicle_theft: 180, other_theft: 1800, fraud: 850, property_damage: 480, drug_offences: 250 } },
];

// Years available
export const YEARS = Array.from({ length: 11 }, (_, i) => 2014 + i);
export const LATEST_YEAR = 2024;
export const PREV_YEAR = 2023;
